/**
 * Google OAuth 관련 핸들러 함수들
 */

import axios from 'axios';
import type { User } from '@edusense/shared-types-user';
import type { AuthResponse, TOKEN_KEY, REFRESH_TOKEN_KEY, ID_TOKEN_KEY } from './types';

export interface OAuthHandlersConfig {
  tokenManager: { setToken: (token: string) => void };
  apiService: { setToken: (token: string) => void };
  isVSCodeEnv: boolean;
  fetchUserProfile: (token: string) => Promise<void>;
  setAuthenticatedState?: (
    user: User, 
    accessToken: string, 
    refreshToken?: string, 
    expiresIn?: number,
    tokenType?: string,
    idToken?: string
  ) => Promise<void>;
}

/**
 * Google Token Exchange 함수
 */
export const createGoogleTokenExchange = (config: OAuthHandlersConfig) => 
  async (googleIdToken: string): Promise<AuthResponse> => {
    console.log('[OAuth] Google Token Exchange 시작');

    try {
      const AUTH_SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL || 'https://auth.ugot.uk';
      const tokenEndpoint = `${AUTH_SERVER_URL}/api/proxy/token`;

      const params = new URLSearchParams({
        grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
        subject_token: googleIdToken,
        subject_token_type: 'urn:ietf:params:oauth:token-type:id_token',
        client_id: 'app-client',
        scope: 'openid profile email api.read offline_access'
      });

      console.log('[OAuth] Token exchange request to proxy:', tokenEndpoint);

      // axios 인스턴스 생성
      const axiosInstance = axios.create();

      const response = await axiosInstance.post(tokenEndpoint, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        withCredentials: true,
        maxRedirects: 0,
        validateStatus: (status) => status >= 200 && status < 400
      });

      // 응답이 배열 형태인 경우 처리 (Java LinkedHashMap serialization issue)
      let tokenData = response.data;
      if (Array.isArray(response.data) && response.data.length === 2 && response.data[0] === 'java.util.LinkedHashMap') {
        tokenData = response.data[1];
        console.log('[OAuth] Detected Java LinkedHashMap response, extracting data:', tokenData);
      }

      if (tokenData && tokenData.access_token) {
        const { access_token, refresh_token, id_token, expires_in } = tokenData;

        // 토큰 저장
        config.tokenManager.setToken(access_token);
        config.apiService.setToken(access_token);

        if (!config.isVSCodeEnv) {
          localStorage.setItem(TOKEN_KEY, access_token);
          if (refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
          }
          if (id_token) {
            localStorage.setItem(ID_TOKEN_KEY, id_token);
          }
        }

        // 사용자 프로필 가져오기
        await config.fetchUserProfile(access_token);

        // setAuthenticatedState가 제공되고 웹 환경인 경우, ID 토큰과 함께 상태 업데이트
        if (config.setAuthenticatedState && !config.isVSCodeEnv) {
          const storedUser = localStorage.getItem('edusense_auth_user');
          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              console.log('[OAuth] Google Token Exchange - setAuthenticatedState 호출 with ID token:', !!id_token);
              await config.setAuthenticatedState(
                user, 
                access_token, 
                refresh_token, 
                expires_in, 
                'Bearer', 
                id_token // ID 토큰을 포함하여 전달
              );
            } catch (error) {
              console.error('[OAuth] Google Token Exchange - setAuthenticatedState 호출 실패:', error);
            }
          }
        }

        return {
          success: true,
          data: {
            access_token,
            refresh_token,
            id_token,
            expires_in
          }
        };
      } else if (tokenData && tokenData.error) {
        console.error('[OAuth] Token exchange error response:', tokenData);
        return {
          success: false,
          message: tokenData.error_description || tokenData.error || '토큰 교환에 실패했습니다.'
        };
      } else {
        console.error('[OAuth] Token exchange response missing access_token:', response.data);
        return {
          success: false,
          message: '토큰 교환 응답이 올바르지 않습니다.'
        };
      }
    } catch (error: any) {
      console.error('[OAuth] Google token exchange failed:', error);

      if (error.response?.data?.error) {
        const errorData = error.response.data;
        return {
          success: false,
          message: errorData.error_description || errorData.error || '토큰 교환에 실패했습니다.'
        };
      }

      return {
        success: false,
        message: error.message || 'Google 로그인 중 오류가 발생했습니다.'
      };
    }
  };

/**
 * Google Authorization Code 교환 함수 - 표준 OAuth2 Authorization Code Flow
 */
export const createGoogleAuthCodeExchange = (config: OAuthHandlersConfig) =>
  async (authCode: string): Promise<AuthResponse> => {
    console.log('[OAuth] Google authorization code exchange 시작');

    try {
      const AUTH_SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL || 'https://auth.ugot.uk';
      const tokenEndpoint = `${AUTH_SERVER_URL}/oauth2/token`;

      // PKCE code_verifier 가져오기
      const codeVerifier = localStorage.getItem('pkce_code_verifier');
      if (!codeVerifier) {
        console.error('[OAuth] PKCE code_verifier가 localStorage에 없음');
        return {
          success: false,
          message: 'PKCE 검증자가 누락되었습니다. 다시 로그인해주세요.'
        };
      }

      // 표준 OAuth2 Authorization Code Flow 파라미터
      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: 'app-client',
        code: authCode,
        redirect_uri: `${window.location.origin}/oauth2/redirect`,
        code_verifier: codeVerifier
      });

      console.log('[OAuth] 토큰 교환 요청:', {
        endpoint: tokenEndpoint,
        clientId: 'app-client',
        redirectUri: `${window.location.origin}/oauth2/redirect`,
        codeVerifierLength: codeVerifier.length
      });

      const response = await axios.post(tokenEndpoint, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        withCredentials: true,
        maxRedirects: 0,
        validateStatus: (status) => status >= 200 && status < 400
      });

      // 응답이 배열 형태인 경우 처리
      let tokenData = response.data;
      if (Array.isArray(response.data) && response.data.length === 2 && response.data[0] === 'java.util.LinkedHashMap') {
        tokenData = response.data[1];
      }

      if (tokenData && tokenData.access_token) {
        const { access_token, refresh_token, id_token, expires_in } = tokenData;

        // 토큰 저장
        config.tokenManager.setToken(access_token);
        config.apiService.setToken(access_token);

        if (!config.isVSCodeEnv) {
          localStorage.setItem(TOKEN_KEY, access_token);
          if (refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
          }
          if (id_token) {
            localStorage.setItem(ID_TOKEN_KEY, id_token);
          }
        }

        // 사용자 프로필 가져오기
        await config.fetchUserProfile(access_token);

        // setAuthenticatedState가 제공되고 웹 환경인 경우, ID 토큰과 함께 상태 업데이트
        if (config.setAuthenticatedState && !config.isVSCodeEnv) {
          const storedUser = localStorage.getItem('edusense_auth_user');
          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              console.log('[OAuth] Google Auth Code - setAuthenticatedState 호출 with ID token:', !!id_token);
              await config.setAuthenticatedState(
                user, 
                access_token, 
                refresh_token, 
                expires_in, 
                'Bearer', 
                id_token // ID 토큰을 포함하여 전달
              );
            } catch (error) {
              console.error('[OAuth] Google Auth Code - setAuthenticatedState 호출 실패:', error);
            }
          }
        }

        return {
          success: true,
          data: {
            access_token,
            refresh_token,
            id_token,
            expires_in
          }
        };
      } else {
        console.error('[OAuth] Token exchange response missing access_token:', response.data);
        return {
          success: false,
          message: tokenData?.error_description || tokenData?.error || '토큰 교환에 실패했습니다.'
        };
      }
    } catch (error: any) {
      console.error('[OAuth] Google auth code exchange failed:', error);
      return {
        success: false,
        message: error.response?.data?.error_description || error.message || 'Google 로그인 중 오류가 발생했습니다.'
      };
    }
  };